import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Clock, CreditCard, CalendarDays, Trash2, Plus, Minus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getDateForDay(dayName) {
  const nowRo = new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/Bucharest' }));
  const currentDay = nowRo.getDay();
  const daysMap = { luni: 1, marti: 2, miercuri: 3, joi: 4, vineri: 5 };
  const targetDay = daysMap[dayName];

  let diff = targetDay - currentDay;
  if (diff < 0) diff += 7;

  const targetDate = new Date(nowRo);
  targetDate.setDate(nowRo.getDate() + diff);
  const y = targetDate.getFullYear();
  const m = String(targetDate.getMonth() + 1).padStart(2, '0');
  const d = String(targetDate.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function addDays(dateStr, days) {
  const d = new Date(dateStr + 'T00:00:00');
  d.setDate(d.getDate() + days);
  return d.toISOString().split('T')[0];
}

function formatDateRo(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  return new Intl.DateTimeFormat('ro-RO', { day: 'numeric', month: 'long' }).format(d);
}

// Show only 12:30–14:30 start slots (i.e. 12:30-13:00 through 14:30-15:00)
function isSlotVisible(slot) {
  const start = slot.split('-')[0];
  return start >= '12:30' && start < '15:00';
}

const DELIVERY_MIN_ORDER = 50; // RON

const Checkout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const stateCart = location.state?.cart;
  const stateDay = location.state?.selectedDay;

  const restored = React.useMemo(() => {
    if (stateCart && stateCart.length > 0) return null;
    const params = new URLSearchParams(window.location.search);
    if (params.get('cancelled') !== '1') return null;
    try {
      const saved = JSON.parse(sessionStorage.getItem('g8_checkout_cart') || 'null');
      return saved && saved.cart && saved.cart.length > 0 ? saved : null;
    } catch {
      return null;
    }
  }, [stateCart]);

  const [cart, setCart] = useState(() => stateCart || restored?.cart || []);
  const selectedDay = stateDay || restored?.selectedDay || 'luni';
  const wasCancelled = !!restored;

  const hasOfertaZilei = cart.some(item => item.category === 'Oferta zilei');
  const OFERTA_ZILEI_ALLOWED_SLOTS = new Set(['12:00-12:30', '12:30-13:00']);

  const updateCartItemQty = (itemId, delta) => {
    setCart(prev => {
      const updated = prev
        .map(it => it.id === itemId ? { ...it, quantity: it.quantity + delta } : it)
        .filter(it => it.quantity > 0);
      return updated;
    });
  };
  const removeCartItem = (itemId) => setCart(prev => prev.filter(it => it.id !== itemId));

  const [formData, setFormData] = useState({
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    pickup_time: '',
  });
  const [deliveryType, setDeliveryType] = useState('pickup'); // 'pickup' | 'delivery'
  const [deliveryData, setDeliveryData] = useState({
    full_name: '',
    phone: '',
    email: '',
    company: '',
    address: '',
    floor: '',
    instructions: '',
  });
  const [loading, setLoading] = useState(false);
  const [pickupSlots, setPickupSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(true);
  const [paymentMethod, setPaymentMethod] = useState('online'); // 'online' | 'pickup'
  const [pickupDate, setPickupDate] = useState(() => getDateForDay(selectedDay));
  const [isNextDay, setIsNextDay] = useState(false);

  // Keep localStorage in sync with every cart edit
  useEffect(() => {
    localStorage.setItem('g8_cart', JSON.stringify(cart));
  }, [cart]);

  // Redirect to menu when cart becomes empty
  useEffect(() => {
    if (cart.length === 0 && !loading) {
      toast.info('Coșul este gol. Te redirecționăm la meniu...');
      navigate('/');
    }
  }, [cart, loading, navigate]);

  useEffect(() => {
    if (wasCancelled) {
      toast.info('Plata a fost anulată. Coșul tău a fost păstrat.', { id: 'g8-checkout-cancelled' });
    }
  }, [wasCancelled]);

  // Load persisted customer + delivery data from localStorage
  useEffect(() => {
    const savedName = localStorage.getItem('cantina_customer_name');
    const savedPhone = localStorage.getItem('cantina_customer_phone');
    const savedEmail = localStorage.getItem('cantina_customer_email');

    if (savedName || savedPhone || savedEmail) {
      setFormData(prev => ({
        ...prev,
        customer_name: savedName || '',
        customer_phone: savedPhone || '',
        customer_email: savedEmail || '',
      }));
    }

    setDeliveryData(prev => ({
      ...prev,
      full_name: savedName || '',
      phone: savedPhone || '',
      email: savedEmail || '',
      company: localStorage.getItem('cantina_delivery_company') || '',
      address: localStorage.getItem('cantina_delivery_address') || '',
      floor: localStorage.getItem('cantina_delivery_floor') || '',
      instructions: localStorage.getItem('cantina_delivery_instructions') || '',
    }));
  }, []);

  useEffect(() => {
    const fetchSlots = async () => {
      setSlotsLoading(true);
      try {
        const baseDate = getDateForDay(selectedDay);
        let { data } = await axios.get(`${API}/pickup-slots?date=${baseDate}`);
        let finalDate = baseDate;
        let daysAhead = 0;
        if (!data.some(s => s.available)) {
          for (let offset = 1; offset <= 6; offset++) {
            const candidate = addDays(baseDate, offset);
            const resp = await axios.get(`${API}/pickup-slots?date=${candidate}`);
            if (resp.data.some(s => s.available)) {
              data = resp.data;
              finalDate = candidate;
              daysAhead = offset;
              break;
            }
          }
        }
        setPickupSlots(data);
        setPickupDate(finalDate);
        setIsNextDay(daysAhead > 0);
      } catch (err) {
        console.error('Error loading slots', err);
        setPickupSlots([]);
      } finally {
        setSlotsLoading(false);
      }
    };
    fetchSlots();
    const iv = setInterval(fetchSlots, 15000);
    return () => clearInterval(iv);
  }, [selectedDay]);

  // Auto-preselect first available visible slot
  useEffect(() => {
    if (formData.pickup_time || pickupSlots.length === 0) return;
    const nowRo = new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/Bucharest' }));
    if (nowRo.getHours() < 12 && !isNextDay) return;
    const visibleSlots = pickupSlots.filter(s => {
      const visible = isSlotVisible(s.slot);
      if (hasOfertaZilei) return OFERTA_ZILEI_ALLOWED_SLOTS.has(s.slot) && visible;
      return visible;
    });
    const firstAvailable = visibleSlots.find(s => s.available);
    if (firstAvailable) {
      setFormData(prev => ({ ...prev, pickup_time: firstAvailable.slot }));
    }
  }, [pickupSlots, formData.pickup_time, isNextDay, hasOfertaZilei]);

  const getTotalAmount = () => {
    return cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (deliveryType === 'delivery') {
      if (!deliveryData.full_name || !deliveryData.phone || !deliveryData.company || !deliveryData.address) {
        toast.error('Te rugăm completează toate câmpurile obligatorii de livrare');
        return;
      }
    } else {
      if (!formData.customer_name || !formData.customer_phone) {
        toast.error('Te rugăm completează numele și telefonul');
        return;
      }
    }

    if (!formData.pickup_time) {
      toast.error('Te rugăm selectează intervalul de ridicare');
      return;
    }

    // Persist to localStorage
    if (deliveryType === 'delivery') {
      localStorage.setItem('cantina_customer_name', deliveryData.full_name);
      localStorage.setItem('cantina_customer_phone', deliveryData.phone);
      if (deliveryData.email) localStorage.setItem('cantina_customer_email', deliveryData.email);
      if (deliveryData.company) localStorage.setItem('cantina_delivery_company', deliveryData.company);
      if (deliveryData.address) localStorage.setItem('cantina_delivery_address', deliveryData.address);
      if (deliveryData.floor) localStorage.setItem('cantina_delivery_floor', deliveryData.floor);
      if (deliveryData.instructions) localStorage.setItem('cantina_delivery_instructions', deliveryData.instructions);
    } else {
      localStorage.setItem('cantina_customer_name', formData.customer_name);
      localStorage.setItem('cantina_customer_phone', formData.customer_phone);
      if (formData.customer_email) localStorage.setItem('cantina_customer_email', formData.customer_email);
    }

    setLoading(true);
    try {
      const customerName = deliveryType === 'delivery' ? deliveryData.full_name : formData.customer_name;
      const customerPhone = deliveryType === 'delivery' ? deliveryData.phone : formData.customer_phone;
      const customerEmail = deliveryType === 'delivery' ? deliveryData.email : formData.customer_email;

      const payload = {
        customer_name: customerName,
        customer_email: customerEmail || '',
        customer_phone: customerPhone,
        pickup_time: formData.pickup_time,
        order_date: pickupDate,
        selected_day: selectedDay,
        delivery_type: deliveryType,
        delivery_details: deliveryType === 'delivery' ? {
          full_name: deliveryData.full_name,
          phone: deliveryData.phone,
          email: deliveryData.email || '',
          company: deliveryData.company,
          address: deliveryData.address,
          floor: deliveryData.floor || '',
          instructions: deliveryData.instructions || '',
        } : null,
        items: cart.map(item => ({
          menu_item_id: typeof item.id === 'string' && item.id.includes('_') ? null : item.id,
          product_id: item.product_id,
          recurring: !!item.recurring,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          choices: item.choices || [],
          reservation_id: item.reservation_id || null,
        })),
      };

      if (paymentMethod === 'pickup') {
        const { data } = await axios.post(`${API}/checkout/cash`, payload);
        localStorage.removeItem('g8_cart');
        sessionStorage.removeItem('g8_checkout_cart');
        navigate(`/track/${data.order_id}`);
      } else {
        const { data } = await axios.post(`${API}/checkout/stripe/create`, payload);
        if (data.checkout_url) {
          sessionStorage.setItem('g8_checkout_cart', JSON.stringify({ cart, selectedDay }));
          window.location.href = data.checkout_url;
        } else {
          throw new Error('Nu s-a putut iniția plata');
        }
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.response?.data?.detail || 'Eroare la finalizarea comenzii');
      setLoading(false);
    }
  };

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-[#E8E4DD] flex items-center justify-center px-4">
        <div className="text-center">
          <p className="text-[#999] mb-4">Coșul este gol</p>
          <Button onClick={() => navigate('/')} className="accent-button">
            Înapoi la Meniu
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#E8E4DD]">
      <header className="sticky top-0 z-50 bg-white border-b border-[#E8E8E8]">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <button
            data-testid="back-button"
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-[#666] hover:text-[#1F1F1F] transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Înapoi
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6">
        <h1 className="text-2xl font-semibold text-[#1F1F1F] mb-6">
          Finalizează Comanda
        </h1>

        {/* Order summary */}
        <div className="minimalist-card p-5 mb-6">
          <h3 className="text-base font-semibold text-[#1F1F1F] mb-4">Rezumatul comenzii</h3>
          {cart.map((item) => (
            <div key={item.id} className="py-3 border-b border-[#F0F0F0] last:border-0">
              <div className="flex justify-between items-start gap-3">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-[#1F1F1F]">{item.name}</p>
                  {(item.choices || []).length > 0 && (
                    <ul className="mt-1 space-y-0.5">
                      {item.choices.map((c, ci) => (
                        <li key={ci} className="text-xs text-[#718096]">
                          <span className="font-medium text-[#5C8945]">{c.group_title}:</span>{' '}
                          {c.selections.map(s => s.name + (s.extra_price > 0 ? ` (+${s.extra_price} lei)` : '')).join(', ')}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="flex flex-col items-end gap-2 shrink-0">
                  <p className="font-semibold text-[#7BA05B] text-sm whitespace-nowrap">
                    {(item.price * item.quantity).toFixed(2)} RON
                  </p>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      data-testid={`checkout-decrease-${item.id}`}
                      onClick={() => updateCartItemQty(item.id, -1)}
                      className="w-7 h-7 flex items-center justify-center rounded-md border border-[#E8E8E8] bg-white hover:bg-[#F5F5F5] text-[#666] transition-colors"
                      title="Scade cantitate"
                    >
                      <Minus className="w-3 h-3" />
                    </button>
                    <span className="text-sm font-bold text-[#1F1F1F] w-6 text-center" data-testid={`checkout-qty-${item.id}`}>
                      {item.quantity}
                    </span>
                    <button
                      type="button"
                      data-testid={`checkout-increase-${item.id}`}
                      onClick={() => updateCartItemQty(item.id, 1)}
                      className="w-7 h-7 flex items-center justify-center rounded-md border border-[#E8E8E8] bg-white hover:bg-[#F5F5F5] text-[#666] transition-colors"
                      title="Crește cantitate"
                    >
                      <Plus className="w-3 h-3" />
                    </button>
                    <button
                      type="button"
                      data-testid={`checkout-remove-${item.id}`}
                      onClick={() => removeCartItem(item.id)}
                      className="w-7 h-7 flex items-center justify-center rounded-md border border-[#FECACA] bg-white hover:bg-[#FEF2F2] text-[#EF4444] transition-colors ml-1"
                      title="Șterge produs"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
          <div className="flex justify-between items-center pt-4 mt-2 border-t-2 border-[#E8E8E8]">
            <p className="text-lg font-semibold text-[#1F1F1F]">Total</p>
            <p className="text-2xl font-bold text-[#7BA05B]" data-testid="checkout-total">{getTotalAmount().toFixed(2)} RON</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="minimalist-card p-5 space-y-5">

          {/* ── Delivery type toggle ── */}
          <div>
            <h3 className="text-sm font-semibold text-[#2D3748] mb-3">Cum doriți să primiți comanda?</h3>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                data-testid="delivery-type-pickup"
                onClick={() => setDeliveryType('pickup')}
                className={`flex items-center gap-2 p-3 rounded-xl border-2 text-left transition-all ${
                  deliveryType === 'pickup'
                    ? 'border-[#7BA05B] bg-[#F5F9F1]'
                    : 'border-[#E8E8E8] bg-white hover:border-[#C5DFB5]'
                }`}
              >
                <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
                  deliveryType === 'pickup' ? 'border-[#7BA05B]' : 'border-[#C8C0B8]'
                }`}>
                  {deliveryType === 'pickup' && <div className="w-2 h-2 rounded-full bg-[#7BA05B]" />}
                </div>
                <span className="text-sm font-medium text-[#2D3748]">Ridicare din locație</span>
              </button>
              <button
                type="button"
                data-testid="delivery-type-delivery"
                onClick={() => setDeliveryType('delivery')}
                className={`flex items-center gap-2 p-3 rounded-xl border-2 text-left transition-all ${
                  deliveryType === 'delivery'
                    ? 'border-[#7BA05B] bg-[#F5F9F1]'
                    : 'border-[#E8E8E8] bg-white hover:border-[#C5DFB5]'
                }`}
              >
                <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
                  deliveryType === 'delivery' ? 'border-[#7BA05B]' : 'border-[#C8C0B8]'
                }`}>
                  {deliveryType === 'delivery' && <div className="w-2 h-2 rounded-full bg-[#7BA05B]" />}
                </div>
                <span className="text-sm font-medium text-[#2D3748]">Livrare la adresă</span>
              </button>
            </div>
          </div>

          {/* ── Customer details (pickup) OR Delivery details ── */}
          {deliveryType === 'pickup' ? (
            <>
              <div>
                <Label htmlFor="name" className="text-[#1F1F1F] mb-2 block font-medium">Nume complet *</Label>
                <Input
                  data-testid="customer-name-input"
                  id="name"
                  value={formData.customer_name}
                  onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                  placeholder="Introduceți numele"
                  className="rounded-lg border-[#E8E8E8] focus:border-[#7BA05B]"
                  required
                />
              </div>
              <div>
                <Label htmlFor="phone" className="text-[#1F1F1F] mb-2 block font-medium">Telefon *</Label>
                <Input
                  data-testid="customer-phone-input"
                  id="phone"
                  type="tel"
                  value={formData.customer_phone}
                  onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                  placeholder="07XX XXX XXX"
                  className="rounded-lg border-[#E8E8E8] focus:border-[#7BA05B]"
                  required
                />
              </div>
              <div>
                <Label htmlFor="email" className="text-[#1F1F1F] mb-2 block font-medium">
                  Email <span className="text-xs text-[#999] font-normal">(pentru confirmare + notificări)</span>
                </Label>
                <Input
                  data-testid="customer-email-input"
                  id="email"
                  type="email"
                  value={formData.customer_email}
                  onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                  placeholder="nume@exemplu.ro"
                  className="rounded-lg border-[#E8E8E8] focus:border-[#7BA05B]"
                />
              </div>
            </>
          ) : (
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-[#2D3748]">Detalii livrare</h3>
              <div>
                <Label className="text-[#1F1F1F] mb-2 block font-medium">Nume complet *</Label>
                <Input
                  data-testid="delivery-name-input"
                  value={deliveryData.full_name}
                  onChange={(e) => setDeliveryData({ ...deliveryData, full_name: e.target.value })}
                  placeholder="Introduceți numele"
                  className="rounded-lg border-[#E8E8E8] focus:border-[#7BA05B]"
                />
              </div>
              <div>
                <Label className="text-[#1F1F1F] mb-2 block font-medium">Telefon *</Label>
                <Input
                  data-testid="delivery-phone-input"
                  type="tel"
                  value={deliveryData.phone}
                  onChange={(e) => setDeliveryData({ ...deliveryData, phone: e.target.value })}
                  placeholder="07XX XXX XXX"
                  className="rounded-lg border-[#E8E8E8] focus:border-[#7BA05B]"
                />
              </div>
              <div>
                <Label className="text-[#1F1F1F] mb-2 block font-medium">Email *</Label>
                <Input
                  data-testid="delivery-email-input"
                  type="email"
                  value={deliveryData.email}
                  onChange={(e) => setDeliveryData({ ...deliveryData, email: e.target.value })}
                  placeholder="nume@exemplu.ro"
                  className="rounded-lg border-[#E8E8E8] focus:border-[#7BA05B]"
                />
              </div>
              <div>
                <Label className="text-[#1F1F1F] mb-2 block font-medium">Companie / Clădire *</Label>
                <Input
                  data-testid="delivery-company-input"
                  value={deliveryData.company}
                  onChange={(e) => setDeliveryData({ ...deliveryData, company: e.target.value })}
                  placeholder="Ex: Clădirea Omega, Birou Central"
                  className="rounded-lg border-[#E8E8E8] focus:border-[#7BA05B]"
                />
              </div>
              <div>
                <Label className="text-[#1F1F1F] mb-2 block font-medium">Adresă de livrare *</Label>
                <Input
                  data-testid="delivery-address-input"
                  value={deliveryData.address}
                  onChange={(e) => setDeliveryData({ ...deliveryData, address: e.target.value })}
                  placeholder="Strada, numărul"
                  className="rounded-lg border-[#E8E8E8] focus:border-[#7BA05B]"
                />
              </div>
              <div>
                <Label className="text-[#1F1F1F] mb-2 block font-medium">
                  Etaj / Birou <span className="text-xs text-[#999] font-normal">(opțional)</span>
                </Label>
                <Input
                  data-testid="delivery-floor-input"
                  value={deliveryData.floor}
                  onChange={(e) => setDeliveryData({ ...deliveryData, floor: e.target.value })}
                  placeholder="Ex: Etaj 3, Birou 304"
                  className="rounded-lg border-[#E8E8E8] focus:border-[#7BA05B]"
                />
              </div>
              <div>
                <Label className="text-[#1F1F1F] mb-2 block font-medium">
                  Instrucțiuni livrare <span className="text-xs text-[#999] font-normal">(opțional)</span>
                </Label>
                <Input
                  data-testid="delivery-instructions-input"
                  value={deliveryData.instructions}
                  onChange={(e) => setDeliveryData({ ...deliveryData, instructions: e.target.value })}
                  placeholder="Ex: Sună la interfon 12"
                  className="rounded-lg border-[#E8E8E8] focus:border-[#7BA05B]"
                />
              </div>
            </div>
          )}

          {/* ── Pickup / delivery interval ── */}
          <div>
            <Label htmlFor="pickup" className="text-[#1F1F1F] mb-2 block font-medium flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Interval ridicare *
            </Label>
            {isNextDay && (
              <div
                data-testid="next-day-badge"
                className="mb-2 inline-flex items-center gap-1.5 text-xs font-semibold text-[#5C8945] bg-[#F5F9F1] border border-[#7BA05B]/30 rounded-full px-3 py-1.5"
              >
                <CalendarDays className="w-3.5 h-3.5" />
                Programare pentru {pickupDate === addDays(getDateForDay(selectedDay), 1) ? 'Mâine' : 'data de'}, {formatDateRo(pickupDate)}
              </div>
            )}
            <Select
              value={formData.pickup_time}
              onValueChange={(value) => setFormData({ ...formData, pickup_time: value })}
            >
              <SelectTrigger data-testid="pickup-time-select" className="rounded-lg border-[#E8E8E8] focus:border-[#7BA05B]">
                <SelectValue placeholder={slotsLoading ? 'Se încarcă intervalele...' : 'Selectează intervalul *'} />
              </SelectTrigger>
              <SelectContent>
                {pickupSlots.filter(s => {
                  const visible = isSlotVisible(s.slot);
                  if (hasOfertaZilei) return OFERTA_ZILEI_ALLOWED_SLOTS.has(s.slot) && visible;
                  return visible;
                }).filter(s => s.available).length === 0 && !slotsLoading && (
                  <div className="px-3 py-2 text-sm text-[#999]">Nu mai există intervale disponibile pentru această zi.</div>
                )}
                {pickupSlots
                  .filter(s => {
                    const visible = isSlotVisible(s.slot);
                    if (hasOfertaZilei) return OFERTA_ZILEI_ALLOWED_SLOTS.has(s.slot) && visible;
                    return visible;
                  })
                  .map((s) => (
                    <SelectItem
                      key={s.slot}
                      value={s.slot}
                      disabled={!s.available}
                      data-testid={`pickup-slot-${s.slot}`}
                    >
                      <div className="flex items-center gap-2">
                        <span className={s.available ? '' : 'text-[#CBD5E0]'}>{s.slot}</span>
                        {!s.available && s.reason === 'full' && <span className="text-[10px] text-red-500 font-semibold">SOLD OUT</span>}
                        {!s.available && s.reason === 'past' && <span className="text-[10px] text-[#A0AEC0]">indisponibil</span>}
                        {s.available && s.orders_count > 0 && (
                          <span className="text-[10px] text-[#A0AEC0]">{s.capacity - s.orders_count} locuri</span>
                        )}
                      </div>
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>

          {/* Packaging notice */}
          <div data-testid="packaging-notice" className="bg-[#FFFBEB] border border-[#FDE68A] rounded-lg p-4">
            <p className="text-sm text-[#92400E]">
              <span className="font-semibold">La pachet:</span> Comenzile sunt pregătite exclusiv la pachet. Dacă vrei, le poți savura cu drag și la noi în sală, în limita locurilor libere.
            </p>
          </div>

          {/* ── Payment Method ── */}
          <div>
            <h3 className="text-sm font-semibold text-[#2D3748] mb-3">Metodă de plată</h3>
            <div className="grid grid-cols-1 gap-3">

              {/* Online */}
              <button
                type="button"
                data-testid="payment-method-online"
                onClick={() => setPaymentMethod('online')}
                className={`flex items-start gap-3 p-4 rounded-xl border-2 text-left transition-all ${
                  paymentMethod === 'online'
                    ? 'border-[#7BA05B] bg-[#F5F9F1]'
                    : 'border-[#E8E8E8] bg-white hover:border-[#C5DFB5]'
                }`}
              >
                <div className={`w-5 h-5 rounded-full border-2 flex-shrink-0 mt-0.5 flex items-center justify-center ${
                  paymentMethod === 'online' ? 'border-[#7BA05B]' : 'border-[#C8C0B8]'
                }`}>
                  {paymentMethod === 'online' && <div className="w-2.5 h-2.5 rounded-full bg-[#7BA05B]" />}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 font-semibold text-[#2D3748]">
                    <CreditCard className="w-4 h-4 text-[#5C8945]" />
                    Plată online securizată
                  </div>
                  <p className="text-xs text-[#718096] mt-1">Visa, Mastercard, Apple Pay, Google Pay via Stripe</p>
                </div>
              </button>

              {/* Cash */}
              <button
                type="button"
                data-testid="payment-method-pickup"
                onClick={() => setPaymentMethod('pickup')}
                className={`flex items-start gap-3 p-4 rounded-xl border-2 text-left transition-all ${
                  paymentMethod === 'pickup'
                    ? 'border-[#7BA05B] bg-[#F5F9F1]'
                    : 'border-[#E8E8E8] bg-white hover:border-[#C5DFB5]'
                }`}
              >
                <div className={`w-5 h-5 rounded-full border-2 flex-shrink-0 mt-0.5 flex items-center justify-center ${
                  paymentMethod === 'pickup' ? 'border-[#7BA05B]' : 'border-[#C8C0B8]'
                }`}>
                  {paymentMethod === 'pickup' && <div className="w-2.5 h-2.5 rounded-full bg-[#7BA05B]" />}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 font-semibold text-[#2D3748]">
                    Plată numerar
                  </div>
                  <p className="text-xs text-[#718096] mt-1">Cash la momentul ridicării sau livrării</p>
                </div>
              </button>

              {/* Meal vouchers — coming soon */}
              <button
                type="button"
                data-testid="payment-method-vouchers"
                disabled
                className="flex items-start gap-3 p-4 rounded-xl border-2 border-[#E8E8E8] bg-[#FAFAFA] text-left opacity-60 cursor-not-allowed"
              >
                <div className="w-5 h-5 rounded-full border-2 border-[#C8C0B8] flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="flex items-center gap-2 font-semibold text-[#2D3748]">
                    Plată cu bonuri de masă
                    <span className="text-[10px] font-medium bg-[#E8F5E9] text-[#4CAF50] px-2 py-0.5 rounded-full">În curând</span>
                  </div>
                  <p className="text-xs text-[#718096] mt-1">Ticket Restaurant, Sodexo, Up Dejun</p>
                </div>
              </button>

            </div>
          </div>

          {/* Minimum order warning for delivery */}
          {deliveryType === 'delivery' && getTotalAmount() < DELIVERY_MIN_ORDER && (
            <div
              data-testid="delivery-min-order-warning"
              className="bg-[#FEF3C7] border border-[#FDE68A] rounded-lg px-4 py-3"
            >
              <p className="text-sm text-[#92400E]">
                Comanda minimă pentru livrare este de <span className="font-semibold">{DELIVERY_MIN_ORDER} lei</span>. Mai adaugă produse în valoare de{' '}
                <span className="font-semibold">{(DELIVERY_MIN_ORDER - getTotalAmount()).toFixed(2)} lei</span>.
              </p>
            </div>
          )}

          <Button
            data-testid="submit-order-button"
            type="submit"
            disabled={loading || (deliveryType === 'delivery' && getTotalAmount() < DELIVERY_MIN_ORDER)}
            className="w-full accent-button py-3 text-base font-medium"
          >
            {loading
              ? 'Se procesează...'
              : paymentMethod === 'pickup'
                ? 'Plasează comanda'
                : 'Plătește Acum'
            }
          </Button>
        </form>
      </main>
    </div>
  );
};

export default Checkout;
